<template>
  <span
    class="tooltip"
    :class="popupClasses"
  >
    <span
      class="tooltip__content"
      :tabindex="disabled ? null : '0'"
      :aria-describedby="id"
      @mousedown="handleMouseDown"
      @focus="handleFocus"
      @blur="handleBlur"
    >
      <slot />
    </span>
    <span
      v-show="!disabled"
      :id="id"
      class="tooltip__popup"
      role="tooltip"
    >
      <slot name="popup" />
    </span>
  </span>
</template>

<script>
export default {
  name: 'CTooltip',

  props: {
    disabled: Boolean,
    position: {
      type: Object,
      default: undefined
    }
  },

  data () {
    return {
      hasFocus: false
    }
  },

  computed: {
    popupClasses () {
      if (this.disabled) {
        return 'is-disabled'
      }

      const classes = []

      for (const size in this.position) {
        classes.push(`tooltip--position-${size}-${this.position[size]}`)
      }

      if (this.hasFocus) {
        classes.push('has-focus')
      }

      return classes
    },
    id () {
      return 'tooltip-' + this._uid
    }
  },

  methods: {
    handleMouseDown (event) {
      event.preventDefault()

      if (document.activeElement === event.currentTarget) {
        document.activeElement.blur()
      } else {
        event.currentTarget.focus()
      }
    },
    handleFocus () {
      this.hasFocus = true
    },
    handleBlur () {
      this.hasFocus = false
    }
  }
}
</script>

<style lang="scss">
@import "../css/variables";

.tooltip {
  position: relative;
  display: inline-block;

  &:not(.is-disabled) {
    cursor: help;
  }
}

.tooltip__content {
  display: inline-block;
}

.tooltip__popup {
  position: absolute;
  left: 50%;
  bottom: 100%;
  width: 12rem;
  padding: $gutter-xs;
  transform: translate(-50%, -1em);
  z-index: 1;

  font-size: 0.875em;
  text-align: center;
  white-space: normal;
  color: #fff;

  background-color: $color-secondary-dark;

  visibility: hidden;
  opacity: 0;

  &::before,
  &::after {
    content: "";
    position: absolute;
  }

  &::before {
    width: 1em;
    height: 1em;
    bottom: -0.5em;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);

    background-color: $color-secondary-dark;
  }

  &::after {
    display: block;
    top: 100%;
    left: 0;
    right: 0;
    height: 1em;
  }

  .tooltip.has-focus & {
    visibility: visible;
    opacity: 1;

    transition-delay: 0s;
  }

  @media (prefers-reduced-motion: no-preference) {
    transition:
      visibility 0.2s 0.2s,
      opacity 0.2s 0.2s;
  }

  @media (min-width: $breakpoint-md) {
    width: 14rem;
    padding: $gutter-sm;

    &::before {
      width: 1.375em;
      height: 1.375em;
      bottom: -0.6875em;
    }
  }
}

@each $size, $breakpoint in $breakpoint-list {
  @media (min-width: $breakpoint) {
    .tooltip--position-#{$size}-left .tooltip__popup {
      left: auto;
      right: 0;
      transform: translate(0, -1em);

      &::before {
        left: auto;
        right: 0.3125em;
        transform: translateX(0) rotate(45deg);
      }
    }

    .tooltip--position-#{$size}-center .tooltip__popup {
      left: 50%;
      right: auto;
      transform: translate(-50%, -1em);

      &::before {
        left: 50%;
        right: auto;
        transform: translateX(-50%) rotate(45deg);
      }
    }

    .tooltip--position-#{$size}-right .tooltip__popup {
      left: 0;
      right: auto;
      transform: translate(0, -1em);

      &::before {
        left: 0.3125em;
        right: auto;
        transform: translateX(0) rotate(45deg);
      }
    }
  }
}
</style>
